export type Account = {
  id: string;
  account_id: string;
  name: string;
  currency: string;
  account_status: number;
};

export type Row = {
  level: 'campaign' | 'adset' | 'ad';
  account_id: string;
  account_name?: string;
  campaign_id?: string;
  campaign_name?: string;
  adset_id?: string;
  adset_name?: string;
  ad_id?: string;
  ad_name?: string;
  date_start?: string;
  date_stop?: string;
  spend: number;
  impressions: number;
  clicks: number;
  link_clicks: number;
  reach: number;
  frequency: number;
  ctr: number;
  cpc: number;
  cpm: number;
  conversions: number;
  conversion_value: number;
  cpa: number;
  roas: number;
  conversion_rate: number;
  publisher_platform?: string;
  device_platform?: string;
  country?: string;
  creative_id?: string;
  creative_name?: string;
  creative_thumbnail?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  destination_url?: string;
};

export type UserRole = 'admin' | 'member';
export type UserStatus = 'active' | 'revoked';

export type AuthUser = {
  email: string;
  name?: string;
  picture?: string;
  sub: string;
  role: UserRole;
};

export type ManagedUser = {
  email: string;
  name: string | null;
  role: UserRole;
  status: UserStatus;
  invited_by: string | null;
  invited_at: number;
  first_login_at: number | null;
  last_login_at: number | null;
};

export class UnauthorizedError extends Error {
  constructor() {
    super('unauthenticated');
    this.name = 'UnauthorizedError';
  }
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin', ...init });
  if (res.status === 401) throw new UnauthorizedError();
  const json = await res.json();
  if (!res.ok || (json && (json as any).error)) {
    throw new Error((json as any)?.error || `HTTP ${res.status}`);
  }
  return json as T;
}

export type ServiceKey = {
  id: string;
  name: string;
  prefix: string;
  created_at: number;
  last_used_at: number | null;
};

export type AuditActorKind = 'session' | 'service_key' | 'dev' | 'anonymous' | 'system';

export type AuditEntry = {
  id: number;
  ts: number;
  actor_email: string | null;
  actor_kind: AuditActorKind;
  actor_id: string | null;
  action: string;
  resource: string | null;
  status: number | null;
  ip: string | null;
  user_agent: string | null;
  method: string | null;
  path: string | null;
  details: string | null;
};

export type WhatsAppChat = {
  remoteJid: string;
  name: string;
  pushName?: string;
  profilePicUrl?: string;
  unreadCount: number;
  isGroup: boolean;
  lastMessage?: string;
  lastMessageAt?: number;
  lastMessageFromMe?: boolean;
};

export type AgentStage = 'qualification' | 'conversion' | 'seller_handoff' | 'closed';

export type AgentLeadData = {
  lead_type?: 'pf' | 'pj' | 'unknown';
  company_size?: number;
  contact_name?: string;
  company_name?: string;
  role?: string;
  phone?: string;
  email?: string;
  cnpj?: string;
  interests?: string[];
  training_offered?: boolean;
  demo_offered?: boolean;
  training_enrolled?: boolean;
  training_enrolled_at?: number;
  // Quando true, Roberta não responde automaticamente nesse chat — humano
  // assume. Pode vir da tool `handoff_to_sales` (auto) ou do botão da UI.
  agent_paused?: boolean;
  agent_paused_at?: number;
  paused_manually?: boolean;
  handoff_reason?: string;
  handoff_notified_at?: number;
};

export type AgentLeadInfo = {
  remoteJid: string;
  stage: AgentStage | string;
  leadData: AgentLeadData;
  createdAt: number;
  updatedAt: number;
  lastInboundAt: number | null;
  lastOutboundAt: number | null;
};

export type WhatsAppMessage = {
  id: string;
  remoteJid: string;
  fromMe: boolean;
  pushName?: string;
  participant?: string;
  timestamp: number;
  type: string;
  body: string;
  mediaUrl?: string;
  mediaMimeType?: string;
  status?: string;
};

export type AuditQuery = {
  limit?: number;
  before?: number;
  actor?: string;
  action?: string;
  q?: string;
  since?: number;
  until?: number;
};

export const api = {
  me: () => fetchJson<{ user: AuthUser }>('/api/auth/me'),
  logout: () => fetchJson<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  accounts: () => fetchJson<{ data: Account[]; cached: boolean }>('/api/accounts'),
  insights: (p: { account_id: string; level: string; since: string; until: string; breakdown?: string }) =>
    fetchJson<{ data: Row[]; cached: boolean; params: any }>(
      `/api/insights?${new URLSearchParams(p as any).toString()}`
    ),
  utm: (p: { account_id: string; since: string; until: string }) =>
    fetchJson<{ data: Row[]; cached: boolean }>(`/api/utm?${new URLSearchParams(p as any).toString()}`),
  creatives: (p: { account_id: string; since: string; until: string }) =>
    fetchJson<{ data: Row[]; cached: boolean }>(`/api/creatives?${new URLSearchParams(p as any).toString()}`),
  clearCache: () =>
    fetch('/api/cache/clear', { method: 'POST', credentials: 'same-origin' }).then((r) => r.json()),

  // ─── Service keys ────────────────────────────────────────────────────
  listKeys: () => fetchJson<{ data: ServiceKey[] }>('/api/keys'),
  createKey: (name: string) =>
    fetchJson<{ data: ServiceKey; key: string }>('/api/keys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }),
  deleteKey: (id: string) =>
    fetchJson<{ ok: true }>(`/api/keys/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // ─── Users (admin only) ──────────────────────────────────────────────
  listUsers: () => fetchJson<{ data: ManagedUser[] }>('/api/users'),
  inviteUser: (p: { email: string; role: UserRole; skip_email?: boolean; reactivate?: boolean }) =>
    fetchJson<{ data: ManagedUser; email_sent: boolean; email_error?: string }>('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(p),
    }),
  resendInvite: (email: string) =>
    fetchJson<{ ok: true }>(`/api/users/${encodeURIComponent(email)}/resend`, { method: 'POST' }),
  changeUserRole: (email: string, role: UserRole) =>
    fetchJson<{ ok: true }>(`/api/users/${encodeURIComponent(email)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    }),
  removeUser: (email: string) =>
    fetchJson<{ ok: true }>(`/api/users/${encodeURIComponent(email)}`, { method: 'DELETE' }),

  // ─── Audit log (admin only) ──────────────────────────────────────────
  audit: (q: AuditQuery = {}) => {
    const params = new URLSearchParams();
    if (q.limit) params.set('limit', String(q.limit));
    if (q.before) params.set('before', String(q.before));
    if (q.actor) params.set('actor', q.actor);
    if (q.action) params.set('action', q.action);
    if (q.q) params.set('q', q.q);
    if (q.since) params.set('since', String(q.since));
    if (q.until) params.set('until', String(q.until));
    const qs = params.toString();
    return fetchJson<{ data: AuditEntry[] }>(`/api/audit${qs ? `?${qs}` : ''}`);
  },
  auditActions: () => fetchJson<{ data: string[] }>('/api/audit/actions'),

  // ─── WhatsApp / Evolution API ────────────────────────────────────────
  whatsappStatus: () =>
    fetchJson<{ data: { state: string } }>('/api/whatsapp/status'),
  whatsappInfo: () =>
    fetchJson<{
      data: {
        configured: boolean;
        apiUrl: string | null;
        managerUrl: string | null;
        instance: string | null;
        state: string | null;
      };
    }>('/api/whatsapp/info'),
  whatsappConnect: () =>
    fetchJson<{
      data: { base64?: string; pairingCode?: string; code?: string; state?: string };
    }>('/api/whatsapp/instance/connect', { method: 'POST' }),
  whatsappRestart: () =>
    fetchJson<{ ok: true }>('/api/whatsapp/instance/restart', { method: 'POST' }),
  whatsappLogout: () =>
    fetchJson<{ ok: true }>('/api/whatsapp/instance/logout', { method: 'POST' }),
  whatsappDeleteInstance: () =>
    fetchJson<{ ok: true }>('/api/whatsapp/instance', { method: 'DELETE' }),
  whatsappChats: () => fetchJson<{ data: WhatsAppChat[] }>('/api/whatsapp/chats'),
  whatsappAgentLead: (remoteJid: string) =>
    fetchJson<{ data: AgentLeadInfo | null }>(
      `/api/whatsapp/agent-lead?remoteJid=${encodeURIComponent(remoteJid)}`
    ),
  whatsappAgentPause: (remoteJid: string, paused: boolean) =>
    fetchJson<{ data: { remoteJid: string; paused: boolean; pausedAt: number | null } }>(
      '/api/whatsapp/agent-pause',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remoteJid, paused }),
      }
    ),
  whatsappAgentReset: (remoteJid: string) =>
    fetchJson<{
      data: { remoteJid: string; messagesDeleted: number; conversationsDeleted: number };
    }>('/api/whatsapp/agent-reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ remoteJid }),
    }),
  whatsappMessages: (remoteJid: string, limit = 100) =>
    fetchJson<{ data: WhatsAppMessage[] }>(
      `/api/whatsapp/messages?remoteJid=${encodeURIComponent(remoteJid)}&limit=${limit}`
    ),
  whatsappSendText: (remoteJid: string, text: string) =>
    fetchJson<{ data: { id: string | null } }>('/api/whatsapp/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ remoteJid, text }),
    }),
  whatsappSendMedia: (p: {
    remoteJid: string;
    mediatype: 'image' | 'video' | 'document';
    mimetype: string;
    media: string;
    fileName?: string;
    caption?: string;
  }) =>
    fetchJson<{ data: { id: string | null } }>('/api/whatsapp/send-media', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(p),
    }),
  whatsappSendAudio: (remoteJid: string, audio: string) =>
    fetchJson<{ data: { id: string | null } }>('/api/whatsapp/send-audio', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ remoteJid, audio }),
    }),
  whatsappDeleteMessage: (p: {
    remoteJid: string;
    id: string;
    fromMe: boolean;
    participant?: string;
  }) =>
    fetchJson<{ ok: true }>('/api/whatsapp/messages', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(p),
    }),
};
